import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './StreamViewer.css';



export default function StreamViewer() {
  const { taskUuid } = useParams();
  const [cards, setCards] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [cardStates, setCardStates] = useState({});
  const [connected, setConnected] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/v1/stream/${taskUuid}/states`)
      .then(r => r.ok ? r.json() : {})
      .then(setCardStates)
      .catch(() => {});
  }, [taskUuid]);

  useEffect(() => {
    if (done) return;
    let stopped = false;
    async function pollCount() {
      while (!stopped) {
        try {
          const res = await fetch(`/api/v1/stream/${taskUuid}/count`);
          if (res.ok) {
            const data = await res.json();
            setTotalCount(data.count);
            if (data.complete) { setDone(true); break; }
          }
        } catch {}
        await new Promise(r => setTimeout(r, 3000));
      }
    }
    pollCount();
    return () => { stopped = true; };
  }, [taskUuid, done]);

  useEffect(() => {
    let cursor = 0;
    let stopped = false;

    async function poll() {
      while (!stopped) {
        try {
          const res = await fetch(`/api/v1/stream/${taskUuid}/cards?cursor=${cursor}`);
          if (!res.ok) throw new Error(res.status);
          const data = await res.json();
          setConnected(true);
          setError(null);

          if (data.cards && data.cards.length > 0) {
            setCards((prev) => [...prev, ...data.cards]);
            cursor = data.next_cursor;
          }

          if (data.complete) {
            setDone(true);
            break;
          }
        } catch (e) {
          setConnected(false);
          setError('Connection lost. Retrying...');
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    }

    poll();
    return () => { stopped = true; };
  }, [taskUuid]);

  const virtuosoRef = useRef(null);

  const jumpToLastViewed = () => {
    const lastIdx = cards.findLastIndex((c) => cardStates[c.card_id]?.viewed);
    if (lastIdx >= 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: lastIdx, align: 'start', behavior: 'smooth' });
    }
  };

  return (
    <div className="stream-viewer">
      <div className="stream-viewer__header">
        <div className="stream-viewer__status">
          <span className={`stream-viewer__dot ${connected ? 'stream-viewer__dot--live' : ''}`} />
          <span>{done ? 'Complete' : connected ? 'Live' : 'Connecting...'}</span>
        </div>
        <button className="stream-viewer__jump" onClick={jumpToLastViewed}>
          To last viewed
        </button>
        <div className="stream-viewer__count">
          {totalCount} error{totalCount !== 1 ? 's' : ''} found
        </div>
      </div>

      {error && !connected && (
        <div className="stream-viewer__error">{error}</div>
      )}

      <Virtuoso
        ref={virtuosoRef}
        style={{ height: 'calc(100vh - 120px)' }}
        className="stream-viewer__cards"
        data={cards}
        defaultItemHeight={500}
        followOutput={false}
        overscan={1000}
        itemContent={(index, card) => (
          <ErrorCard
            card={card}
            taskUuid={taskUuid}
            initialState={cardStates[card.card_id] || {}}
            onViewed={(cardId) => {
              setCardStates((prev) => ({
                ...prev,
                [cardId]: { ...prev[cardId], viewed: true }
              }));
            }}
          />
        )}
        components={{
          Footer: () => !done ? (
            <div className="stream-viewer__loading">
              <div className="stream-viewer__spinner" />
              <span>Loading more results...</span>
            </div>
          ) : null
        }}
      />

      {done && (
        <div className="stream-viewer__footer">
          Review complete. {cards.length} issues identified.
        </div>
      )}
    </div>
  );
}
